import { resolveNotificationCategoryLabel } from "./resolve-notification-category-label.js";

/**
 * Mapeia DTO de notificação da API para ViewModel da UI.
 * UI não conhece `notificationId` / envelopes crus.
 *
 * Preferência de IDs: campos top-level → `metadata` → deeplink.
 *
 * @param {Object} [dto]
 * @returns {{
 *   id: string,
 *   userId: string|null,
 *   type: string,
 *   title: string,
 *   body: string,
 *   channels: string[],
 *   status: string,
 *   isEssential: boolean,
 *   requiresAction: boolean,
 *   isVirtual: boolean,
 *   createdAt: string|null,
 *   readAt: string|null,
 *   deeplink: string|null,
 *   saleId: string|null,
 *   requesterUserId: number|null,
 *   module: string|null,
 *   categoryLabel: string,
 * }}
 */
export function toNotificationVm(dto) {
  const id =
    typeof dto?.notificationId === "string"
      ? dto.notificationId
      : typeof dto?.id === "string"
        ? dto.id
        : "";

  const deeplink =
    typeof dto?.deeplink === "string" && dto.deeplink.trim()
      ? dto.deeplink
      : null;

  const metadata =
    dto?.metadata && typeof dto.metadata === "object" ? dto.metadata : null;
  const context =
    metadata?.context && typeof metadata.context === "object"
      ? metadata.context
      : null;

  const module =
    typeof context?.module === "string" && context.module.trim()
      ? context.module.trim().toLowerCase()
      : null;

  const fromDeeplink = parseSaleIdAndRequesterFromDeeplink(deeplink);

  const saleId =
    normalizeIdString(dto?.saleId) ??
    normalizeIdString(metadata?.saleId) ??
    normalizeIdString(context?.entityId) ??
    fromDeeplink.saleId;

  const requesterUserId =
    parsePositiveInt(dto?.requesterUserId) ??
    parsePositiveInt(dto?.notifyUserId) ??
    parsePositiveInt(dto?.requestedByUserId) ??
    parsePositiveInt(metadata?.requesterUserId) ??
    fromDeeplink.requesterUserId;

  const type = typeof dto?.type === "string" ? dto.type : "";

  return {
    id,
    userId:
      dto?.userId === undefined || dto?.userId === null
        ? null
        : String(dto.userId),
    type,
    title: typeof dto?.title === "string" ? dto.title : "",
    body: typeof dto?.body === "string" ? dto.body : "",
    channels: Array.isArray(dto?.channels)
      ? dto.channels.filter((c) => typeof c === "string")
      : [],
    status: typeof dto?.status === "string" ? dto.status : "pending",
    isEssential: Boolean(dto?.isEssential),
    requiresAction: Boolean(dto?.requiresAction),
    isVirtual: Boolean(dto?.isVirtual),
    createdAt:
      typeof dto?.createdAt === "string" && dto.createdAt.trim()
        ? dto.createdAt
        : null,
    readAt:
      typeof dto?.readAt === "string" && dto.readAt.trim()
        ? dto.readAt
        : null,
    deeplink,
    saleId,
    requesterUserId,
    module,
    categoryLabel: resolveNotificationCategoryLabel({ module, type }),
  };
}

/**
 * Parse local do deeplink (evita import cross-feature sale → notifications).
 * @param {string|null} deeplink
 * @returns {{ saleId: string|null, requesterUserId: number|null }}
 */
function parseSaleIdAndRequesterFromDeeplink(deeplink) {
  if (!deeplink) return { saleId: null, requesterUserId: null };
  try {
    const url = new URL(deeplink, "http://local.invalid");
    const saleMatch = url.pathname.match(/\/sale\/([^/?#]+)/i);
    const saleId = saleMatch?.[1]
      ? decodeURIComponent(saleMatch[1])
      : null;
    const raw =
      url.searchParams.get("notifyUserId") ??
      url.searchParams.get("requesterUserId") ??
      url.searchParams.get("requestedByUserId");
    return {
      saleId,
      requesterUserId: parsePositiveInt(raw),
    };
  } catch {
    return { saleId: null, requesterUserId: null };
  }
}

/** @param {unknown} value @returns {string|null} */
function normalizeIdString(value) {
  if (typeof value === "string" && value.trim()) return value.trim();
  if (value != null && Number.isFinite(Number(value)) && Number(value) > 0) {
    return String(Math.trunc(Number(value)));
  }
  return null;
}

/** @param {unknown} value @returns {number|null} */
function parsePositiveInt(value) {
  if (value === undefined || value === null || value === "") return null;
  const n = typeof value === "number" ? value : Number(String(value).trim());
  if (!Number.isFinite(n) || n <= 0) return null;
  return Math.trunc(n);
}

/**
 * @param {Array} [items]
 * @returns {ReturnType<typeof toNotificationVm>[]}
 */
export function toNotificationVmList(items) {
  if (!Array.isArray(items)) return [];
  return items.map(toNotificationVm).filter((item) => item.id);
}
